// FieldRoutes Sync — first-service appointments and where their FieldRoutes sync
// stands. Counts from GET /sales/fieldroutes/sync-status/, the list from
// /sync-issues/; Retry posts /sync-retry/ (managers only, server-enforced).
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getSyncStatus, getSyncIssues, retrySyncItems } from '../../api/salesApi';
import Button from '../../components/ui/button';
import Badge from '../../components/ui/badge';
import { Table, THead, TRow, TCell, TEmpty } from '../../components/ui/table';
import { getUserRole } from '../../helpers/tenantHelpers';
import RevivalHeader, { StatStrip } from '../../components/Revival/RevivalHeader';
import { count, shortDate } from './salesFormat';
import '../../components/Revival/revival.css';
import './sales-ds.css';

const STATUS = {
  synced: { label: 'Synced', tone: 'success' },
  ready: { label: 'Ready', tone: 'info' },
  pending: { label: 'Pending', tone: 'neutral' },
  blocked: { label: 'Blocked', tone: 'warning' },
  failed: { label: 'Failed', tone: 'danger' },
};
const FILTERS = [['all', 'All'], ['failed', 'Failed'], ['blocked', 'Blocked']];
const MANAGER_ROLES = ['admin', 'owner', 'manager', 'Admin', 'Manager', 'Owner'];

export default function SyncStatusPage() {
  const [counts, setCounts] = useState(null);
  const [issues, setIssues] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const [retryingBulk, setRetryingBulk] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);

  const canRetry = MANAGER_ROLES.includes(getUserRole('Member'));

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusRes, issuesRes] = await Promise.all([
        getSyncStatus(),
        getSyncIssues({ status: filter, limit: 100 }),
      ]);
      setCounts(statusRes?.data?.counts || null);
      setIssues(issuesRes?.data?.items || []);
    } catch (err) {
      setError((err?.response?.status ?? err?.status) === 403
        ? 'You do not have permission to view FieldRoutes sync data, or this tenant is not configured for FieldRoutes.'
        : 'Sync data didn’t load.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRetry = async (appointmentId) => {
    setRetrying((prev) => ({ ...prev, [appointmentId]: true }));
    try {
      await retrySyncItems([appointmentId]);
      toast.success('Retry queued');
      fetchData();
    } catch (err) {
      toast.error((err?.response?.status ?? err?.status) === 403 ? 'Permission denied — manager access required' : 'Retry failed');
    } finally {
      setRetrying((prev) => ({ ...prev, [appointmentId]: false }));
    }
  };

  const handleRetrySelected = async () => {
    if (selected.size === 0) return;
    setRetryingBulk(true);
    try {
      await retrySyncItems([...selected]);
      toast.success(`${selected.size} item(s) queued for retry`);
      setSelected(new Set());
      fetchData();
    } catch (err) {
      toast.error((err?.response?.status ?? err?.status) === 403 ? 'Permission denied — manager access required' : 'Bulk retry failed');
    } finally {
      setRetryingBulk(false);
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected(selected.size === issues.length ? new Set() : new Set(issues.map((i) => i.appointment_id)));

  const columns = [
    ...(canRetry ? [{ label: <input type="checkbox" aria-label="Select all" checked={issues.length > 0 && selected.size === issues.length} onChange={toggleSelectAll} /> }] : []),
    'Customer', 'Service', 'Appointment', 'Status', 'Last error', 'Failed at',
    ...(canRetry ? [''] : []),
  ];

  return (
    <div className="rv-screen">
      <RevivalHeader
        title="FieldRoutes sync"
        totals={counts ? [<><b>{count(counts.total)}</b> first-service appointments</>] : []}
        actions={<Button variant="secondary" size="sm" onClick={fetchData} loading={loading}>Refresh</Button>}
      />
      {error ? <p className="sl-notice is-bad">{error}</p> : null}
      {counts ? (
        <StatStrip stats={Object.entries(STATUS).map(([key, meta]) => ({ label: meta.label, value: count(counts[key]) }))} />
      ) : null}
      {!error ? (
        <section className="sl-section">
          <div className="sl-section-head">
            <h2>Sync issues</h2>
            <p>Failed or blocked syncs.</p>
            <div className="sl-end">
              {canRetry && selected.size > 0 ? (
                <Button variant="secondary" size="sm" onClick={handleRetrySelected} loading={retryingBulk}>
                  Retry {selected.size} selected
                </Button>
              ) : null}
            </div>
          </div>
          <div className="rv-chips" role="toolbar" aria-label="Filter sync issues">
            {FILTERS.map(([f, label]) => (
              <button key={f} type="button" className={`rv-chip${filter === f ? ' is-active' : ''}`} aria-pressed={filter === f}
                onClick={() => { setFilter(f); setSelected(new Set()); }}>
                {label}
              </button>
            ))}
          </div>
          <Table>
            <THead columns={columns} />
            <tbody>
              {issues.length === 0 && !loading ? (
                <TEmpty colSpan={columns.length}>{filter === 'all' ? 'No sync issues.' : `No ${filter} syncs.`}</TEmpty>
              ) : null}
              {issues.map((item) => {
                const meta = STATUS[item.status] || STATUS.pending;
                return (
                  <TRow key={item.appointment_id}>
                    {canRetry ? (
                      <TCell>
                        <input type="checkbox" aria-label={`Select ${item.customer_name || 'appointment'}`}
                          checked={selected.has(item.appointment_id)} onChange={() => toggleSelect(item.appointment_id)} />
                      </TCell>
                    ) : null}
                    <TCell>{item.customer_name || '—'}</TCell>
                    <TCell>{item.service_type || '—'}</TCell>
                    <TCell>{item.appointment_start ? shortDate(item.appointment_start) : '—'}</TCell>
                    <TCell><Badge tone={meta.tone}>{meta.label}</Badge></TCell>
                    <TCell><span className="sl-clip" title={item.last_error || ''}>{item.last_error || '—'}</span></TCell>
                    <TCell>{item.failed_at ? new Date(item.failed_at).toLocaleString() : '—'}</TCell>
                    {canRetry ? (
                      <TCell>
                        <Button variant="secondary" size="sm" onClick={() => handleRetry(item.appointment_id)} loading={retrying[item.appointment_id]}>
                          Retry
                        </Button>
                      </TCell>
                    ) : null}
                  </TRow>
                );
              })}
            </tbody>
          </Table>
        </section>
      ) : null}
    </div>
  );
}
